<script lang="ts">
  import { type StoresKey } from "~/stores";
  import { onMount } from 'svelte';
  import SearchInput_impl from '../search/input/SearchInput.svelte';

  export let request_store_key: StoresKey;
  export let searchOverlayBackgroundColor: string = "#FFFFFF";
  export let searchInputBorderColor: string = "#E0E0E0";
  export let searchInputBorderColorFocused: string = "#424242";
  export let searchInputTextColor: string = "#424242";
  export let searchInputPlaceholderColor: string = "#9E9E9E";
  export let searchInputIconColor: string = "#424242";

  let isSearchOpen = false;
  let searchOverlay: HTMLElement;
  let searchInputRef: HTMLInputElement;
  
  // Setup reactive viewport detection
  let isMobileView = false;

  const browser = typeof window !== 'undefined';

  // Reactive viewport detection
  function updateViewport() {
    if (browser) {
      isMobileView = window.innerWidth < 992;
    }
  }

  $: if (browser) {
    updateViewport();
  }

  function toggleSearch(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    
    if (isSearchOpen) {
      closeSearch();
    } else {
      isSearchOpen = true;
      // Focus the input after a small delay to ensure the element is mounted
      setTimeout(() => {
        searchInputRef?.focus();
      }, 0);
    }
  }

  function closeSearch() {
    isSearchOpen = false;
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isSearchOpen) {
      closeSearch();
    }

    if ((event.key === 'Enter' || event.key === ' ') && event.target) {
      const target = event.target as HTMLElement;
      
      if (target.classList.contains('search-backdrop')) {
        event.preventDefault();
        closeSearch();
      }
    }
  }

  $: if (browser) {
    if (isSearchOpen) {
      document.documentElement.classList.add('demo2025-search-open');
    } else {
      document.documentElement.classList.remove('demo2025-search-open');
    }
  }

  onMount(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSearchOpen && searchOverlay && !searchOverlay.contains(event.target as Node)) {
        const searchButton = document.querySelector('.search-toggle');
        if (!searchButton?.contains(event.target as Node)) {
          isSearchOpen = false;
        }
      }
    };

    // Setup resize handler for viewport detection
    const handleResize = () => {
      updateViewport();
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleResize);
    
    // Initial viewport detection
    updateViewport();
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('resize', handleResize);
      if (browser) {
        document.documentElement.classList.remove('demo2025-search-open');
      }
    };
  });
</script>

<div class="search-toggle-wrapper">
  <button class="search-toggle" on:click={toggleSearch} aria-label="Toggle search">
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  </button>

  {#if isSearchOpen}
    <div 
      class="search-overlay" 
      bind:this={searchOverlay}
      role="dialog"
      aria-label="Search"
    >
      <div 
        class="search-backdrop" 
        on:click={closeSearch} 
        on:keydown={(e) => e.key === 'Enter' || e.key === ' ' ? closeSearch() : null}
        role="button" 
        tabindex="0"
      ></div>
      <div class="search-container">
        <SearchInput_impl
          {request_store_key}
          isOpen={isSearchOpen}
          {closeSearch}
          bind:inputRef={searchInputRef}
          searchOverlayBackgroundColor={searchOverlayBackgroundColor}
          searchInputBorderColor={searchInputBorderColor}
          searchInputBorderColorFocused={searchInputBorderColorFocused}
          searchInputTextColor={searchInputTextColor}
          searchInputPlaceholderColor={searchInputPlaceholderColor}
          searchInputIconColor={searchInputIconColor}
        />
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  .search-toggle-wrapper {
    display: flex;
    align-items: center;
    position: relative;
  }

  .search-toggle {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    -webkit-tap-highlight-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
      color: var(--icon-color, #3B3B3C);
      stroke: currentColor;
      -webkit-appearance: none;
      display: block;
      
      * {
        color: inherit;
        stroke: currentColor;
        -webkit-text-fill-color: currentColor;
      }
    }

    @media screen and (max-width: 991px) {
      svg {
        width: 24px;
        height: 24px;
      }
    }
  }

  .search-overlay {
    position: absolute;
    top: calc(100% + var(--header-padding) + 0.5rem);
    right: 0;
    width: 500px;
    background: var(--search-overlay-background-color, #FFFFFF);
    padding: 0.5rem 1rem;
    z-index: 150;

    @media screen and (max-width: 991px) {
      position: fixed;
      top: var(--header-height, 60px);
      left: 0;
      right: 0;
      width: 100vw;
      padding: 0;
      background: var(--search-overlay-background-color, #FFFFFF);
      z-index: 151;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
  }

  .search-container {
    margin: 0 auto;
    padding: 0;
    display: flex;
    justify-content: flex-end;
    position: relative;
    z-index: 101;
    
    @media screen and (max-width: 991px) {
      justify-content: stretch;
      background: var(--search-overlay-background-color, #FFFFFF);
      padding: 1rem;
      width: 100%;
      max-width: 100%;
    }
  }

  .search-backdrop {
    display: none;
    cursor: pointer;
    
    @media (max-width: 991px) {
      display: block;
      position: fixed;
      top: var(--header-height, 60px);
      left: 0;
      background: rgba(0, 0, 0, 0.3);
      height: calc(100vh - var(--header-height, 60px));
      width: 100vw;
      z-index: 99;
    }
  }

  :global(html.demo2025-search-open) {
    overflow: hidden;
  }
  
  :global(html.demo2025-search-open body) {
    overflow: hidden;
    position: relative;
  }
</style> 